import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordInstanceKeys } from "../record/record.options";
import { assetInstanceKeys, assetKeys } from "./asset.options";
import { assetService } from "./asset.service";
import type {
	AssetParams,
	AssetTypesListParams,
	CreateAssetInfo,
	CreateAssetTypeInfo,
	EditAssetFileInfo,
	EditAssetInfo,
	EditAssetTypeInfo,
	ServiceResult,
} from "./asset.types";

/**
 * Create an asset instance.
 * POST /assets
 */
export const useCreateAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateAssetInfo) => assetService.createAsset(data),
		onSuccess: () => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.counts(),
				}),
			]);
		},
		onError: (error) => {
			console.error("Failed to create asset:", error);
		},
	});
};

/**
 * Update an asset instance with cache invalidation.
 * PUT /assets/{assetId}
 */
export const useUpdateAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: EditAssetInfo }) =>
			assetService.updateAsset(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...assetInstanceKeys.details(), id],
				}),
				queryClient.invalidateQueries({
					queryKey: assetKeys.assetsDetail(id),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.historyCount(id),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.validStatuses(id),
				}),
			]);
			return result;
		},
		onError: (error) => {
			console.error("Failed to update asset:", error);
		},
	});
};

/**
 * Delete an asset instance
 * DELETE /assets/{assetId}
 */
export const useDeleteAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => assetService.deleteAsset(id, { organizationId }),
		onSuccess: (_result, { id }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...assetInstanceKeys.details(), id],
				}),
				queryClient.invalidateQueries({
					queryKey: assetKeys.assetsDetail(id),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.counts(),
				}),
			]);
		},
		onError: (error) => {
			console.error("Failed to delete asset:", error);
		},
	});
};

export const useCreateAssetType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			data,
			organizationId,
		}: {
			data: CreateAssetTypeInfo;
			organizationId?: string;
		}) => assetService.createAssetType(data, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: assetKeys.types(),
			});
		},
		onError: (error) => {
			console.error("Failed to create asset type:", error);
		},
	});
};

/**
 * Duplicate an asset type. Cache refresh matches create; navigation stays with the caller.
 */
export const useDuplicateAssetType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetTypeId,
			organizationId,
		}: {
			assetTypeId: string;
			organizationId?: string;
		}) => assetService.duplicateAssetType(assetTypeId, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: assetKeys.types(),
			});
		},
		onError: (error) => {
			console.error("Failed to duplicate asset type:", error);
		},
	});
};

export const useDeleteAssetType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => assetService.deleteAssetType(id, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: assetKeys.types(),
			});
		},
		onError: (error) => {
			console.error("Failed to delete asset type:", error);
		},
	});
};

export const useUpdateAssetType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetTypeId,
			data,
			organizationId,
		}: {
			assetTypeId: string;
			data: EditAssetTypeInfo;
			organizationId?: string;
		}) => assetService.updateAssetType(assetTypeId, data, { organizationId }),
		onSuccess: (_result, { assetTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: assetKeys.types(),
			});
			queryClient.invalidateQueries({
				queryKey: assetKeys.assetTypeHistory(assetTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to update asset type:", error);
		},
	});
};

export const useEnableAssetType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			assetTypeId,
			organizationId,
		}: {
			assetTypeId: string;
			organizationId?: string;
		}) => assetService.enableAssetType(assetTypeId, { organizationId }),
		onSuccess: (_result, { assetTypeId }) => {
			queryClient.invalidateQueries({ queryKey: assetKeys.types() });
			queryClient.invalidateQueries({
				queryKey: assetKeys.assetTypeHistory(assetTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to enable asset type:", error);
		},
	});
};

export const useDisableAssetType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			assetTypeId,
			organizationId,
		}: {
			assetTypeId: string;
			organizationId?: string;
		}) => assetService.disableAssetType(assetTypeId, { organizationId }),
		onSuccess: (_result, { assetTypeId }) => {
			queryClient.invalidateQueries({ queryKey: assetKeys.types() });
			queryClient.invalidateQueries({
				queryKey: assetKeys.assetTypeHistory(assetTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to disable asset type:", error);
		},
	});
};

export const useEnableAssetTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			assetTypeId,
			subOrgId,
			organizationId,
		}: {
			assetTypeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			assetService.enableAssetTypeSubOrganization(assetTypeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { assetTypeId, organizationId }) => {
			queryClient.invalidateQueries({ queryKey: assetKeys.types() });
			queryClient.invalidateQueries({
				queryKey: assetKeys.typeDetail(assetTypeId, organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: assetKeys.assetTypeHistory(assetTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to enable sub-organization for asset type:", error);
		},
	});
};

export const useDisableAssetTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			assetTypeId,
			subOrgId,
			organizationId,
		}: {
			assetTypeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			assetService.disableAssetTypeSubOrganization(assetTypeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { assetTypeId, organizationId }) => {
			queryClient.invalidateQueries({ queryKey: assetKeys.types() });
			queryClient.invalidateQueries({
				queryKey: assetKeys.typeDetail(assetTypeId, organizationId),
			});
			queryClient.invalidateQueries({
				queryKey: assetKeys.assetTypeHistory(assetTypeId),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to disable sub-organization for asset type:",
				error,
			);
		},
	});
};

/**
 * Export asset types to CSV
 * GET /assets/types/{start}/{count}/export
 */
export const useExportAssetTypes = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: AssetTypesListParams & { columns?: string };
		}) => {
			const { columns, ...filters } = params;
			return assetService.exportAssetTypesCsv(
				startIndex,
				count,
				filters,
				columns,
			);
		},
		onError: (error) => {
			console.error("Asset types export failed:", error);
		},
	});
};

/**
 * Export asset instances to CSV.
 * GET /assets/{start}/{count}/export
 */
export const useExportAssets = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: AssetParams & { columns?: string };
		}) => {
			return assetService.exportAssets(startIndex, count, params);
		},
		onError: (error) => {
			console.error("Assets export failed:", error);
		},
	});
};

/**
 * Upload an asset attachment.
 * POST /assets/{assetId}/files
 */
export const useUploadAssetFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			file,
			organizationId,
			description,
			entityAttachmentRequirementTagId,
			onUploadProgress,
		}: {
			assetId: string;
			file: File | Blob;
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		}) =>
			assetService.uploadAssetFile(assetId, file, {
				organizationId,
				description,
				entityAttachmentRequirementTagId,
				onUploadProgress,
			}),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.files(assetId),
			});
		},
	});
};

/**
 * Delete an asset attachment.
 * DELETE /assets/{assetId}/files/{fileId}
 */
export const useDeleteAssetFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			fileId,
			organizationId,
		}: {
			assetId: string;
			fileId: string;
			organizationId?: string;
		}) => assetService.deleteAssetFile(assetId, fileId, { organizationId }),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.files(assetId),
			});
		},
	});
};

/**
 * Rename / update an asset attachment.
 * PUT /assets/{assetId}/files/{assetFileId}
 */
export const useEditAssetFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			fileId,
			name,
			description,
			organizationId,
		}: {
			assetId: string;
			fileId: string;
			name?: string | null;
			description?: string | null;
			organizationId?: string;
		}) =>
			assetService.editAssetFile(
				assetId,
				fileId,
				{ name, description } satisfies EditAssetFileInfo,
				{ organizationId },
			),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.files(assetId),
			});
		},
	});
};

/**
 * Create an asset note.
 * POST /assets/{assetId}/notes
 */
export const useCreateAssetNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			text,
			organizationId,
		}: {
			assetId: string;
			text: string;
			organizationId?: string;
		}) => assetService.createAssetNote(assetId, { text }, { organizationId }),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.notes(assetId),
			});
		},
	});
};

/**
 * Update an asset note.
 * PUT /assets/{assetId}/notes/{noteId}
 */
export const useUpdateAssetNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			noteId,
			text,
			organizationId,
		}: {
			assetId: string;
			noteId: string;
			text: string;
			organizationId?: string;
		}) =>
			assetService.updateAssetNote(
				assetId,
				noteId,
				{ text },
				{ organizationId },
			),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.notes(assetId),
			});
		},
	});
};

/**
 * Delete an asset note.
 * DELETE /assets/{assetId}/notes/{noteId}
 */
export const useDeleteAssetNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			noteId,
			organizationId,
		}: {
			assetId: string;
			noteId: string;
			organizationId?: string;
		}) => assetService.deleteAssetNote(assetId, noteId, { organizationId }),
		onSuccess: (_result, { assetId }) => {
			queryClient.invalidateQueries({
				queryKey: assetInstanceKeys.notes(assetId),
			});
		},
	});
};

/**
 * Link a record to an asset.
 * POST /assets/{assetId}/records/{recordId}
 */
export const useLinkAssetRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			recordId,
			organizationId,
		}: {
			assetId: string;
			recordId: string;
			organizationId?: string;
		}) => assetService.linkAssetRecord(assetId, recordId, { organizationId }),
		onSuccess: (_result, { assetId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.records(assetId),
				}),
				queryClient.invalidateQueries({
					queryKey: [...assetInstanceKeys.details(), assetId],
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

/**
 * Unlink a record from an asset.
 * DELETE /assets/{assetId}/records/{recordId}
 */
export const useUnlinkAssetRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			assetId,
			recordId,
			organizationId,
		}: {
			assetId: string;
			recordId: string;
			organizationId?: string;
		}) => assetService.unlinkAssetRecord(assetId, recordId, { organizationId }),
		onSuccess: (_result, { assetId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: assetInstanceKeys.records(assetId),
				}),
				queryClient.invalidateQueries({
					queryKey: [...assetInstanceKeys.details(), assetId],
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

export const assetMutations = {
	createAsset: useCreateAsset,
	updateAsset: useUpdateAsset,
	deleteAsset: useDeleteAsset,
	createAssetType: useCreateAssetType,
	duplicateAssetType: useDuplicateAssetType,
	deleteAssetType: useDeleteAssetType,
	updateAssetType: useUpdateAssetType,
	exportAssets: useExportAssets,
	exportAssetTypes: useExportAssetTypes,
	linkRecord: useLinkAssetRecord,
	unlinkRecord: useUnlinkAssetRecord,
	uploadFile: useUploadAssetFile,
	deleteFile: useDeleteAssetFile,
	editFile: useEditAssetFile,
	createNote: useCreateAssetNote,
	updateNote: useUpdateAssetNote,
	deleteNote: useDeleteAssetNote,
};
